import { Alert, Box, Typography, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AgGridReact, CustomCellRendererProps } from 'ag-grid-react';
import { CellEditRequestEvent, ColDef } from 'ag-grid-community';
import { useGetSettings, useUpdateSettings } from '../hooks/api/use-settings.hook';
import { SettingsApiModel } from '../../../types/models/settings.model';
import { CustomTheme } from '../contexts/theme.context';
import { ImageUploadEditor } from '../components/AgGrid/ImageUploadEditor';

type SettingRow = { name: keyof SettingsApiModel; value: SettingsApiModel[keyof SettingsApiModel] };

const rowHeight = 70;
const baseImageUrl = import.meta.env.VITE_CLOUDFRONT_URL;

export const Settings = () => {
  const theme = useTheme<CustomTheme>();
  const { loading, error, settings } = useGetSettings();
  const [updateSettings, { loading: updateLoading, error: updateError }] = useUpdateSettings();

  const columns: ColDef<SettingRow>[] = [
    {
      field: 'name',
      cellStyle: { lineHeight: `${rowHeight}px` },
      valueGetter: ({ data }) =>
        // Convert camel case name to human readable format
        data?.name
          ?.match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
          ?.map((x) => x.slice(0, 1).toUpperCase() + x.slice(1).toLowerCase())
          ?.join(' '),
    },
    {
      field: 'value',
      cellStyle: { lineHeight: `${rowHeight}px` },
      // FIXME: need better logic to handle displaying different settings, right now its just based on the name
      cellEditorSelector: (params) =>
        params.data?.name === 'companyLogo' ? { component: ImageUploadEditor } : undefined,
      cellRenderer: ({ node }: CustomCellRendererProps<SettingRow>) =>
        node.data?.name === 'companyLogo' && node.data?.value ? (
          <img
            src={`${baseImageUrl}${node.data?.value}`}
            alt="Company logo"
            style={{
              verticalAlign: 'middle',
              backgroundColor: theme.palette.primary.main,
              height: '50px',
              padding: '2px',
            }}
          />
        ) : (
          node.data?.value
        ),
    },
  ];

  return (
    <Box sx={{ height: '100%' }}>
      <Outlet />
      <Helmet title={'Settings'} />
      <Box display="flex" flexDirection="column" sx={{ height: '100%', p: '0 12px' }}>
        <Box display="flex" sx={{ m: '10px 0' }}>
          <Typography variant="h5">Manage Settings</Typography>
        </Box>
        {updateError ? <Alert severity="error">{updateError?.response?.data.message}</Alert> : null}

        <Box alignSelf={'stretch'} flexGrow={1} sx={{ pb: '12px' }}>
          {error ? (
            <Alert severity="error">{error?.response?.data.message}</Alert>
          ) : (
            <AgGridReact
              loading={loading || updateLoading}
              columnDefs={columns}
              defaultColDef={{ flex: 1, minWidth: 100, sortable: false, editable: true }}
              rowData={Object.entries(settings || {}).map(([name, value]) => ({ name, value }))}
              readOnlyEdit={true}
              rowHeight={rowHeight}
              onCellEditRequest={(event: CellEditRequestEvent<SettingRow>) =>
                updateSettings({ [event.data.name]: event.newValue })
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
